<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useEventDay } from '@/composables/eventDay';

    const props = defineProps({
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: '',
        },
    });
    const { item } = toRefs(props);

    const { eventDay } = useEventDay(item.value);

    // The event's own photo when there is one, otherwise the flat colour swatch
    // this card has always used. Field names mirror the `images` block on
    // server/collections/events.ts, so anything the sync ingests to R2 is
    // reachable here.
    const { getImageFromArray } = useImageUrl();
    const IMAGE_FIELDS = ['Images', 'Image', 'Photo', 'Photos', 'Featured Image', 'Event Photo', 'Event Image', 'Program Image'];

    const uploadedImage = computed(() => {
        const fields = item.value?.fields ?? {};
        for (const key of IMAGE_FIELDS) {
            const value = fields[key];
            if (Array.isArray(value) && value.length) {
                const url = getImageFromArray(value, 0);
                if (url) return url;
            }
        }
        return '';
    });

    // /img/events/sat.jpg and sun.jpg are not photographs — they are solid
    // swatches, #015b5a and #4a4a4a, which is where the card's white date text
    // gets its ~3.3:1 contrast. Keep them as the fallback so an event with no
    // photo looks exactly as it does today.
    const fallbackImage = computed(() => (eventDay?.num === 6 ? '/img/events/sat.jpg' : '/img/events/sun.jpg'));
    const cardImage = computed(() => uploadedImage.value || fallbackImage.value);

    const { sitesMap } = storeToRefs(useSitesStore());
    const siteId = computed(() => item.value.fields?.Site?.[0]);
    const siteData = computed(() => sitesMap.value?.[siteId.value]);

    const route = useRoute();

    const url = computed(() => {
        const id = item.value?.id;
        if (route.path.includes('archive')) {
            return '/archive/2023/events/' + id;
        } else return '/events/' + id;
    });

    const membersOnly = computed(() => {
        return !!item?.value?.fields?.['Members Only'];
    });
</script>

<template>
    <li class="event" :class="type">
            <div class="image-with-date" v-if="eventDay && eventDay.num">
                <img :src="cardImage" alt="" />
                <!-- Only over an uploaded photo: the fallback swatches already
                     ARE this colour, so tinting them would darken twice. -->
                <div
                    v-if="uploadedImage"
                    class="tint"
                    :class="eventDay.num === 6 ? 'is-saturday' : 'is-sunday'"></div>
                <div class="text">
                    <p class="month uppercase bold">October</p>
                    <ClientOnly v-if="eventDay && (eventDay.label || eventDay.date)">
                        <p class="day uppercase bold">{{ eventDay.label }}</p>
                        <p class="date uppercase bold">{{ eventDay.date }}</p>
                    </ClientOnly>
                </div>
            </div>
            <div class="details">
                <div class="details-container">
                    <div class="tag-with-time">
                        <SvgsMembersOnly v-if="membersOnly" class="tag" />
                        <p class="time" v-if="item.fields['Time']">
                            {{ item.fields['Time'] }}
                        </p>
                    </div>
                    <NuxtLink :to="url">
                        <h3 class="title">{{ item.fields['Program Title'] }}</h3>
                    </NuxtLink>
                    <div class="marker-with-venue" v-if="siteData?.fields?.Name">
                        <SvgsThinMarker class="marker" />
                        <p class="venue">{{ siteData?.fields?.Name }}</p>
                    </div>
                </div>

                <GlobalUserActionButton :item="item" list="ohcFavoriteEvents" />
            </div>
    </li>
</template>

<style lang="scss" scoped>
    
    .active-card .event {
        background-color: $white;
    }
    
    .event {
        list-style-type: none;
        border-bottom: 1px solid #d1d1d1;
        padding: 0 0 20px 0;

        @media (min-width: $desktop) {
            padding: 0;
        }

        .details {
            padding-top: 20px;
            display: flex;
            flex-direction: row;
            gap: 16px;

            button {
                margin-left: auto;
            }

            @media (min-width: $desktop) {
                flex-direction: column;
                height: 100%;

                .details-container {
                    height: 100%;
                }
                
                button {
                    margin-left: 0;
                    margin-top: auto;
                }                
            }
        }

        &:has(.image-with-date) {
            border-bottom: none;

            .details { 
                padding-top: 0px; 
                height: calc(100% - 100px);
            }
        }

        @media (min-width: $tablet) {
            &:not(.map) {
                border: 1px solid #d1d1d1;
            }
        }

        &.map {
            padding: 15px;
            margin-bottom: 0;
            display: flex;
            gap: 16px;
            flex-direction: row;
            height: 100%;
            border-bottom: none;            

            .inner {
                gap: 16px;

                @media (min-width: $tablet) {
                    flex-direction: row;
                }                
            }

            h3.title {
                overflow: hidden;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                line-clamp: 2;
                -webkit-box-orient: vertical;
                text-overflow: ellipsis;
                margin-bottom: 16px;

                @media (min-width: $desktop) {
                    margin-bottom: 8px;
                }
            }

            .image-with-date {
                flex: 0 0 100px;
                display: none;

                @media (min-width: $desktop) {
                    width: 120px;
                    display: block;
                }
            }

            .details {
                padding: 0;
                display: flex;
                flex-direction: column;
                height: 100%;

                button {
                    margin-top: auto;
                }

                .tag-with-time {
                    display: block;
                    
                    .time {
                        margin-top: 8px;
                        font-size: 12px;
                        font-weight: 350;
                        line-height: 1em;
                        text-transform: uppercase;
                    }

                    @media (min-width: $desktop) {
                        display: flex;
                        align-items: center;
                        margin-top: 10px;

                        .time {
                            font-size: 15px;
                            margin-top: 0px;
                        }
                    }
                }

                .title {
                    font-size: 18px;
                    font-weight: 800;
                    line-height: 21px;
                }
            }
        }

        .image-with-date {
            position: relative;
            flex: 0 0 100px;
            height: 100px;
            margin-bottom: 8px;

            @media (min-width: $tablet) {
                flex: 0 0 150px;
            }

            img,
            .tint {
                height: 100%;
            }

            img,
            .tint,
            .text {
                position: absolute;
                width: 100%;
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            // `multiply` rather than a translucent overlay, because uploaded
            // photos are never contrast-checked. Multiply can only ever darken:
            // a pure white photo composites to exactly the swatch colour, so the
            // white date text keeps AT LEAST the ~3.3:1 it has on the fallback
            // today, and anything darker only improves it. A plain alpha tint
            // would need ~95% opacity to make the same guarantee, which would
            // hide the photograph entirely.
            .tint {
                mix-blend-mode: multiply;
                pointer-events: none;

                &.is-saturday {
                    background: #015b5a;
                }

                &.is-sunday {
                    background: #4a4a4a;
                }
            }

            .text {
                top: 50%;
                transform: translateY(-50%);
                color: $white;
                text-align: center;
                font-family: $title;

                .month {
                    font-size: 10px;
                    line-height: 1.4;

                    @media (min-width: $tablet) {
                        font-size: 12px;
                    }
                }

                .day {
                    font-size: 15px;
                    line-height: 1.6;

                    @media (min-width: $tablet) {
                        font-size: 20px;
                        line-height: 1;
                    }
                }

                .date {
                    font-size: 24px;
                    line-height: 0.875;

                    @media (min-width: $tablet) {
                        font-size: 20px;
                    }
                }
            }
        }

        .details {

            @media (min-width: $tablet) {
                padding: 15px;
                display: flex;
                flex-direction: column;
            }

            .tag-with-time {
                display: flex;
                margin-bottom: 5px;

                .tag {
                    margin-right: 10px;
                }

                .time {
                    font-size: 12px;
                    font-weight: $regular;
                    line-height: 2.3;
                    letter-spacing: 0.72px;
                }
            }

            .title {
                font-size: 21px;
                font-weight: $heavy;
                line-height: 1.3;

                @media (min-width: $tablet) {
                    margin-bottom: 10px;
                    font-size: 18px;
                    line-height: 20px;
                }
            }

            .marker-with-venue {
                display: flex;
                margin-bottom: 10px;
                .marker {
                    height: 14px;
                    margin-right: 5px;
                }
                
                .venue {
                    font-size: 14px;
                    font-weight: $book;
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 1;
                    -webkit-box-orient: vertical;
                    text-overflow: ellipsis;

                    @media (min-width: $desktop) {
                        font-size: 16px;
                    }
                }
            }

            .heart-add-button {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 34px;
                height: 34px;
                border: 1px solid #9d9fa2;
                border-radius: 100%;
            }
        }

        @media (min-width: $desktop) {
            .tag-with-time {
                svg {
                    width: 120px;
                    height: 28px;
                }
            }

            &:not(.map) .details-container {
                margin-bottom: 20px;
            }
        }
    }
</style>
